import { useState, useCallback, useEffect, useRef } from 'react';

interface UseKeyboardNavigationOptions {
  resultCount: number;
  onSelect: (index: number) => void;
  isOpen: boolean;
}

export function useKeyboardNavigation({
  resultCount,
  onSelect,
  isOpen,
}: UseKeyboardNavigationOptions) {
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0);
  }, [resultCount]);

  // Reset when palette closes
  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(0);
    }
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (resultCount === 0) return;

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          setActiveIndex((prev) => (prev + 1) % resultCount);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          setActiveIndex((prev) => (prev - 1 + resultCount) % resultCount);
          break;
        }
        case 'Enter': {
          e.preventDefault();
          if (activeIndex < resultCount) {
            onSelect(activeIndex);
          }
          break;
        }
      }
    },
    [resultCount, activeIndex, onSelect],
  );

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const activeEl = listRef.current.querySelector(`[data-index="${activeIndex}"]`);
    if (activeEl) {
      activeEl.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  return { activeIndex, setActiveIndex, handleKeyDown, listRef };
}
